import { DieManagement_DieAllocationTourSteps } from "../../components/TourGuide/tourGuideData";
import { useEffect, useRef, useState } from "react";
import { dieMasterAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import { BsSendFill } from "react-icons/bs";
import { formatDateTime } from "../../utlis/formateDate";
import { STATUS_COLORS } from "../../utlis/constant";
import { RegistryHeader, Button as SharedButton, CalculationFooter } from "../../common/Shared";
import { TourGuide } from "../../components/TourGuide/TourGuide";
import { Info } from "lucide-react";

const DieAllocation = () => {
  const getDieImageUrl = (row) => {
    if (!row) return "";
    const imgUrl = row.die_Image || row.dieImage || row.Die_Image;
    if (imgUrl) return imgUrl;

    const pdfUrl = row.die_Pdf || row.diePdf || row.Die_Pdf;
    if (pdfUrl && typeof pdfUrl === "string") {
      return pdfUrl.replace(/\/Pdf\//i, "/Image/").replace(/\.pdf$/i, ".jpg");
    }

    const dieId = row.dieId || row.die_Id || row.Die_Id;
    if (dieId) {
      return `https://hline.blob.core.windows.net/hlinefiles/Extrusion/DieMedia/Image/${dieId}.jpg`;
    }
    return "";
  };

  const getDiePdfUrl = (row) => {
    if (!row) return "";
    const pdfUrl = row.die_Pdf || row.diePdf || row.Die_Pdf;
    if (pdfUrl) return pdfUrl;

    const dieId = row.dieId || row.die_Id || row.Die_Id;
    if (dieId) {
      return `https://hline.blob.core.windows.net/hlinefiles/Extrusion/DieMedia/Pdf/${dieId}.pdf`;
    }
    return "";
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [isDiePreparedData, setIsDiePreparedData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("die-preparation");
  const [preparedPage, setPreparedPage] = useState(1);
  const [preparedHasMore, setPreparedHasMore] = useState(true);
  const [sendOvenData, setSendOvenData] = useState([]);
  const [sendOvenPage, setSendOvenPage] = useState(1);
  const [sendOvenHasMore, setSendOvenHasMore] = useState(true);
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);

  const handleTourComplete = () => {
    localStorage.setItem("aluex_dieallocation_tour_seen", "true");
  };

  const fetchDiePreparationData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllDieAllocation(page, pageSize);

      setIsDiePreparedData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setPreparedHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchSendtoovenData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllOvenIssuedData(page, pageSize);

      setSendOvenData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setSendOvenHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "die-preparation") {
      setPreparedPage(1);
      setIsDiePreparedData([]);
      setPreparedHasMore(true);
    } else {
      setSendOvenPage(1);
      setSendOvenData([]);
      setSendOvenHasMore(true);
    }
  };

  useEffect(() => {
    if (activeTab === "die-preparation") fetchDiePreparationData(preparedPage);
  }, [preparedPage, activeTab]);

  useEffect(() => {
    if (activeTab === "send-to-oven") fetchSendtoovenData(sendOvenPage);
  }, [sendOvenPage, activeTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (activeTab === "die-preparation" && preparedHasMore) {
          setPreparedPage((p) => p + 1);
        }

        if (activeTab === "send-to-oven" && sendOvenHasMore) {
          setSendOvenPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTab, loading, preparedHasMore, sendOvenHasMore]);

  const handleSendToOven = async () => {
    if (selectedIds.length === 0) {
      notify("Please select at least one die", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        dieIds: selectedIds,
        createdBy: Number(userId),
      };

      await dieMasterAPI.sendToOven(payload);

      notify(`Successfully sent ${selectedIds.length} dies to Oven`, "success");

      setSelectedIds([]);
      setPreparedPage(1);
      setIsDiePreparedData([]);
      fetchDiePreparationData(1);

      setSendOvenPage(1);
      setSendOvenData([]);
      fetchSendtoovenData(1);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to send to oven";
      notify(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const ovenColumns = [
    {
      key: "dieNumber",
      label: "Die no",
      align: "center",
      render: (val, row) => {
        const pdfUrl = getDiePdfUrl(row);
        return pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-black hover:underline cursor-pointer text-[12px] uppercase tracking-tighter"
          >
            {val}
          </a>
        ) : (
          <span>{val}</span>
        );
      }
    },
    { key: "currentWeight", label: "Current Weight", align: "center", },
    {
      key: "status_Name",
      label: "Die Status",
      align: "center",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location", align: "center", },
    {
      key: "oven_Start_Datetime",
      label: "Oven Time",
      align: "center",
      render: (value) => formatDateTime(value),
    },
  ];

  const columns = [
    {
      key: "select",
      disableSort: true,
      align: "center",
      label: (
        <input
          type="checkbox"
          className="w-4 h-4 text-primary cursor-pointer"
          checked={
            isDiePreparedData.length > 0 &&
            selectedIds.length === isDiePreparedData.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = isDiePreparedData.map((i) => i.dieId);
              setSelectedIds(allIds);
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      render: (_, row) => (
        <input
          type="checkbox"
          className="w-4 h-4 text-primary cursor-pointer"
          checked={selectedIds.includes(row.dieId)}
          onChange={() => {
            const rowId = row.dieId;
            setSelectedIds((prev) =>
              prev.includes(rowId)
                ? prev.filter((id) => id !== rowId)
                : [...prev, rowId],
            );
          }}
        />
      ),
    },
    {
      key: "dieNumber",
      label: "Die no",
      align: "center",
      render: (val, row) => {
        const imageUrl = getDieImageUrl(row);
        return imageUrl ? (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-black hover:underline cursor-pointer text-[12px] uppercase tracking-tighter"
          >
            {val}
          </a>
        ) : (
          <span>{val}</span>
        );
      }
    },
    { key: "currentWeight", label: "Current Weight", align: "center", },
    {
      key: "status_Name",
      label: "Status", align: "center",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location", align: "center", },
    { key: "oven_Start_Datetime", label: "Oven Time", align: "center", },
  ];

  const renderDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl border-y border-slate-100 p-4">
        <Table
          columns={[
            { key: "order_No", label: "SO NO", align: "left", className: "font-bold text-blue-600 underline cursor-pointer" },
            { key: "profile_Length", label: "Cut Length", align: "center", className: "text-slate-600" },
            { key: "qty", label: "Pcs", align: "right", className: "font-semibold text-slate-800" },
            { key: "qty_Weight", label: "Weight (kg)", align: "right", className: "text-emerald-600 font-bold", render: (val) => Number(val).toFixed(3) },
            { key: "order_Status", label: "Order Status", align: "right", className: "text-blue-600 font-bold" }
          ]}
          data={row.details}
          showActions={false}
          footer={{
            order_No: "Grand Total",
            qty: totalPcs,
            qty_Weight: totalWeight.toFixed(3)
          }}
          tableClassName="text-[12px] border-none font-bold"
        />
      </div>
    );
  };

  const renderDieDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl">
        <Table
          columns={[
            { key: "order_No", label: "SO NO", align: "left", className: "font-bold text-blue-600 underline cursor-pointer" },
            { key: "profile_Length", label: "Cut Length", align: "center", className: "text-slate-600" },
            { key: "qty", label: "Pcs", align: "right", className: "font-semibold text-slate-800" },
            { key: "qty_Weight", label: "Weight (kg)", align: "right", className: "text-emerald-600 font-bold", render: (val) => Number(val).toFixed(3) }
          ]}
          data={row.details}
          showActions={false}
          footer={{
            order_No: "Grand Total",
            qty: totalPcs,
            qty_Weight: totalWeight.toFixed(3)
          }}
          tableClassName="text-[12px] border-none font-bold"
        />
      </div>
    );
  };

  const totalPcs = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const pcs = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty) || 0),
        0,
      );
      return sum + pcs;
    }, 0);

  const totalWeight = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const w = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty_Weight) || 0),
        0,
      );
      return sum + w;
    }, 0)
    .toFixed(3);

  return (
    <div className="flex flex-col h-full bg-white font-plus relative">
      {loading && <Loader centered />}
      <RegistryHeader title="Die Allocation Matrix">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsTourOpen(true)}
            className="flex items-center gap-2 px-6 py-2 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/5 transition-colors border border-primary bg-white shrink-0"
            title="Page Guide"
          >
            <Info size={14} />
            Guide
          </button>
        </div>
        {/* High-Density Tabs */}
        <div className="flex bg-gray-100 p-1 rounded-sm gap-1" data-tour="alloc-tabs">
          <button
            onClick={() => handleTabChange("die-preparation")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "die-preparation"
              ? "bg-primary text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Die Preparation
          </button>
          <button
            onClick={() => handleTabChange("send-to-oven")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "send-to-oven"
              ? "bg-primary text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Send to Oven
          </button>
          {/* <button
            onClick={() => handleTabChange("production-done")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "production-done"
              ? "bg-primary text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Production Done
          </button> */}
        </div>

        {activeTab === "die-preparation" && (
          <>
            <div data-tour="alloc-issue">
              <SharedButton
                disabled={selectedIds.length === 0 || loading}
                onClick={handleSendToOven}
                icon={BsSendFill}
                className="whitespace-nowrap"
              >
                {loading ? "Processing..." : `Issue To Oven [${selectedIds.length}]`}
              </SharedButton>
            </div>
          </>
        )}
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col bg-white" data-tour="alloc-table">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          {activeTab === "die-preparation" ? (
            <div className="min-w-max">
              <Table
                columns={columns.map(col => ({
                  ...col,
                  render: col.key === 'status_Name' ? (val) => {
                    const color = STATUS_COLORS[val] || "bg-gray-100 text-gray-600 border-gray-200";
                    const cleanColor = color.replace(/rounded-full|rounded-md|rounded-lg/g, 'rounded-none');
                    return (
                      <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[2px] border rounded-none ${cleanColor}`}>
                        {val}
                      </span>
                    );
                  } : col.render
                }))}
                data={isDiePreparedData}
                showActions={false}
                renderExpansion={renderDieDetailTable}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          ) : (
            <div className="min-w-max">
              <Table
                columns={ovenColumns.map(col => ({
                  ...col,
                  render: col.key === 'status_Name' ? (val) => {
                    const color = STATUS_COLORS[val] || "bg-gray-100 text-gray-600 border-gray-200";
                    const cleanColor = color.replace(/rounded-full|rounded-md|rounded-lg/g, 'rounded-none');
                    return (
                      <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[2px] border rounded-none ${cleanColor}`}>
                        {val}
                      </span>
                    );
                  } : col.render
                }))}
                data={sendOvenData}
                showActions={false}
                renderExpansion={renderDetailTable}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          )}
        </div>
      </div>

      {/* Standardized Summary Footer */}
      {selectedIds.length > 0 && activeTab === "die-preparation" && (
        <CalculationFooter
          items={[
            {
              label: "SELECTED PCS",
              value: totalPcs.toLocaleString(),
              suffix: "PCS"
            },
            {
              label: "TOTAL WEIGHT",
              value: totalWeight,
              suffix: "KG",
              color: "text-primary"
            }
          ]}
        >
          <button
            onClick={() => setSelectedIds([])}
            className="px-8 py-2.5 text-[10px] font-black uppercase tracking-[3px] text-gray-400 hover:text-red-500 transition-colors border border-gray-200 hover:border-red-200 bg-white"
          >
            Clear
          </button>
          <button
            onClick={handleSendToOven}
            className="bg-primary text-white px-10 py-2.5 text-[10px] font-black uppercase tracking-[3px] hover:bg-gray-800 transition-all  #3F80F7]/20"
          >
            Issue To Oven
          </button>
        </CalculationFooter>
      )}

      <TourGuide
        isOpen={isTourOpen}
        onClose={() => setIsTourOpen(false)}
        steps={DieManagement_DieAllocationTourSteps}
        onComplete={handleTourComplete}
      />
    </div>
  );
};

export default DieAllocation;

